import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { api } from "../services/api";

// Generic query function that uses the API service
export const getQueryFn: <T>(options: {
  on401: "returnNull" | "throw";
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const endpoint = queryKey[0] as string;
      return await api.get<T>(endpoint);
    } catch (error: any) {
      if (unauthorizedBehavior === "returnNull" && error.message?.includes("401")) {
        return null;
      }
      throw error;
    }
  };

// Generic mutation function
export const getMutationFn = <T>(method: 'post' | 'put' | 'delete') => {
  return async ({ endpoint, data }: { endpoint: string; data?: unknown }) => {
    switch (method) {
      case 'post':
        return api.post<T>(endpoint, data);
      case 'put':
        return api.put<T>(endpoint, data);
      case 'delete':
        return api.delete<T>(endpoint);
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Export for backward compatibility
export const apiRequest = async (
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> => {
  // This is kept for backward compatibility but should be replaced with api service
  console.warn('apiRequest is deprecated. Use api service instead.');
  
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {};
  
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${response.status}: ${text}`);
  }
  
  return response;
};
